import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Row, Space, Statistic, Table, Tag, Typography } from "antd";
import { BankOutlined, EuroOutlined } from "@ant-design/icons";
import { LineChart } from "../charts/LineChart";
import { TOKEN_KEY } from "../../authProvider";
import { axiosInstance } from "../../utils";
import {
  mergeRevenueWindows,
  splitRevenueRange,
  type ClubRevenueRow,
  type PlatformRevenueResponse,
} from "./revenueWindows";

interface RevenueAnalyticsProps {
  dateRange: { from: string; to: string };
}

const money = (value: number, currency = "eur") =>
  new Intl.NumberFormat("en-IE", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(value);

export const RevenueAnalytics: React.FC<RevenueAnalyticsProps> = ({ dateRange }) => {
  const rangeKey = `${dateRange.from}|${dateRange.to}`;
  const [reload, setReload] = useState(0);
  const [result, setResult] = useState<{
    key: string;
    data?: PlatformRevenueResponse;
    error?: string;
    loading: boolean;
  }>({ key: rangeKey, loading: true });

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setResult({ key: rangeKey, loading: true });

    const load = async () => {
      try {
        const windows = splitRevenueRange(dateRange.from, dateRange.to);
        const results: PlatformRevenueResponse[] = new Array(windows.length);
        let next = 0;
        const worker = async () => {
          while (next < windows.length && !controller.signal.aborted) {
            const index = next++;
            const response = await axiosInstance.get<PlatformRevenueResponse>(
              `${import.meta.env.VITE_API_URL}/admin/dashboard/revenue`,
              {
                params: windows[index],
                headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
                signal: controller.signal,
              },
            );
            results[index] = response.data;
          }
        };
        await Promise.all(Array.from({ length: Math.min(3, windows.length) }, worker));
        if (active) setResult({ key: rangeKey, data: mergeRevenueWindows(results), loading: false });
      } catch (error) {
        if (active) {
          controller.abort();
          setResult({
            key: rangeKey,
            error: error instanceof Error ? error.message : "Revenue could not be loaded.",
            loading: false,
          });
        }
      }
    };
    void load();
    return () => { active = false; controller.abort(); };
  }, [dateRange.from, dateRange.to, rangeKey, reload]);

  const current: typeof result = result.key === rangeKey ? result : { key: rangeKey, loading: true };
  const analytics = current.loading || current.error ? undefined : current.data;
  const totals = analytics?.totals;
  const currency = analytics?.currency ?? "eur";
  const loading = current.loading;
  const chartData = (analytics?.daily ?? []).flatMap((point) => [
    { date: point.date, count: point.total, type: "Total commission" },
    { date: point.date, count: point.membership, type: "Membership" },
    { date: point.date, count: point.lotto, type: "Lotto" },
  ]);

  if (current.error) {
    return (
      <Col span={24}>
        <Alert
          type="error"
          showIcon
          message="Revenue could not be loaded"
          description={current.error}
          action={<Button size="small" onClick={() => setReload((value) => value + 1)}>Retry</Button>}
        />
      </Col>
    );
  }

  return (
    <Col span={24}>
      <Space direction="vertical" size={24} style={{ width: "100%" }}>
        <div>
          <Typography.Title level={3} style={{ marginBottom: 4 }}>
            B2B commission revenue
          </Typography.Title>
          <Typography.Text type="secondary">
            Live from Stripe application fees collected on club membership and lotto payments.
          </Typography.Text>
        </div>

        <Row gutter={[16, 16]}>
          <MetricCard
            title="Platform revenue"
            value={totals?.platformRevenue ?? 0}
            suffix={currency.toUpperCase()}
            icon={<EuroOutlined />}
            note="Application fees after fee refunds"
            loading={loading}
          />
          <MetricCard
            title="Volume"
            value={totals?.volume ?? 0}
            suffix={currency.toUpperCase()}
            icon={<BankOutlined />}
            note="Net club proceeds, including membership, shop and lotto"
            loading={loading}
          />
          <MetricCard
            title="Membership received"
            value={totals?.membershipReceived ?? 0}
            suffix={currency.toUpperCase()}
            icon={<EuroOutlined />}
            note="Net membership proceeds received by clubs"
            loading={loading}
          />
          <MetricCard
            title="Lotto received"
            value={totals?.lottoReceived ?? 0}
            suffix={currency.toUpperCase()}
            icon={<EuroOutlined />}
            note="Net lotto proceeds received by clubs"
            loading={loading}
          />
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={8}>
            <Card title="Commission mix" loading={loading} style={{ height: "100%" }}>
              <Space direction="vertical" size={18} style={{ width: "100%" }}>
                <Statistic title="Membership commission" value={money(totals?.membershipRevenue ?? 0, currency)} />
                <Statistic title="Lotto commission" value={money(totals?.lottoRevenue ?? 0, currency)} />
                {(totals?.shopRevenue ?? 0) !== 0 && (
                  <Statistic title="Legacy shop commission" value={money(totals?.shopRevenue ?? 0, currency)} />
                )}
                <Typography.Text type="secondary">
                  Customer refunds: {money(totals?.refunded ?? 0, currency)}
                </Typography.Text>
              </Space>
            </Card>
          </Col>
          <Col xs={24} lg={16}>
            <Card title="Commission over time">
              <LineChart
                title=""
                icon={null}
                data={chartData}
                metrics={[
                  { label: "Total", value: totals?.platformRevenue ?? 0, color: "hsl(142 76% 36%)", type: "Total commission" },
                  { label: "Membership", value: totals?.membershipRevenue ?? 0, color: "hsl(215 75% 50%)", type: "Membership" },
                  { label: "Lotto", value: totals?.lottoRevenue ?? 0, color: "hsl(270 70% 50%)", type: "Lotto" },
                ]}
                xField="date"
                yField="count"
                height={280}
                isLoading={loading}
                bare
              />
            </Card>
          </Col>
        </Row>

        {(totals?.unclassifiedRevenue ?? 0) !== 0 && (
          <Alert
            type="warning"
            showIcon
            message={`${money(totals?.unclassifiedRevenue ?? 0, currency)} needs attribution`}
            description="Stripe collected these fees, but their payment metadata did not identify membership or lotto. They remain included in total revenue."
          />
        )}

        <Card title="Revenue by club" extra={<Tag color="green">Stripe reconciled</Tag>}>
          <Table<ClubRevenueRow>
            rowKey="stripeAccountId"
            loading={loading}
            dataSource={analytics?.clubs ?? []}
            pagination={{ pageSize: 20, showSizeChanger: true }}
            scroll={{ x: 1000 }}
            columns={[
              {
                title: "Club",
                dataIndex: "clubName",
                fixed: "left",
                width: 230,
                render: (name: string, row) => (
                  <Space direction="vertical" size={0}>
                    <Typography.Text strong>{name}</Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>{row.stripeAccountId}</Typography.Text>
                  </Space>
                ),
              },
              { title: "Volume", dataIndex: "volume", align: "right", sorter: (a, b) => a.volume - b.volume, render: (value, row) => money(value, row.currency) },
              { title: "Membership received", dataIndex: "membershipReceived", align: "right", sorter: (a, b) => a.membershipReceived - b.membershipReceived, render: (value, row) => money(value, row.currency) },
              { title: "Shop received", dataIndex: "shopReceived", align: "right", sorter: (a, b) => a.shopReceived - b.shopReceived, render: (value, row) => money(value, row.currency) },
              { title: "Lotto received", dataIndex: "lottoReceived", align: "right", sorter: (a, b) => a.lottoReceived - b.lottoReceived, render: (value, row) => money(value, row.currency) },
              {
                title: "GymLive earned",
                dataIndex: "platformRevenue",
                align: "right",
                sorter: (a, b) => a.platformRevenue - b.platformRevenue,
                defaultSortOrder: "descend",
                render: (value, row) => <Typography.Text strong>{money(value, row.currency)}</Typography.Text>,
              },
              {
                title: "Configured commission",
                align: "right",
                render: (_, row) => {
                  const rates = [];
                  if (row.membershipReceived !== 0) rates.push(`${row.membershipCommissionPercent}% membership`);
                  if (row.lottoReceived !== 0) rates.push(`${row.lottoCommissionPercent}% lotto`);
                  return rates.length > 0 ? rates.join(" · ") : "—";
                },
              },
              { title: "Fees", dataIndex: "feeCount", align: "right" },
            ]}
          />
        </Card>
      </Space>
    </Col>
  );
};

function MetricCard({
  title,
  value,
  suffix,
  icon,
  note,
  loading,
  precision = 2,
}: {
  title: string;
  value: number;
  suffix?: string;
  icon: React.ReactNode;
  note: string;
  loading: boolean;
  precision?: number;
}) {
  return (
    <Col xs={24} sm={12} xl={6}>
      <Card loading={loading}>
        <Statistic title={title} value={value} precision={precision} prefix={icon} suffix={suffix} />
        <Typography.Text type="secondary">{note}</Typography.Text>
      </Card>
    </Col>
  );
}
